import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';

// Quote API Interfaces
export interface CreateQuoteRequest {
  agentName?: string;
  insuredName: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  customerNumber?: string;
  loanType: number;
  programId: number;
  insuranceCompanyId?: number;
  policies: PolicyDto[];
}

export interface PolicyDto {
  policyNumber: string;
  coverage: number;
  effectiveDate: Date;
  expirationDate?: Date;
  premiumAmount: number;
  taxesAmount: number;
  feesAmount: number;
  minEarnedPercent?: number;
  minEarnedAmount?: number;
  downAmount?: number;
  cancelDays: number;
  auditable: boolean;
  lossPayeeRequired: boolean;
  lossPayeeRequest: boolean;
}

export interface Quote {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  agentName: string;
  insuredName: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  customerNumber?: string;
  loanType: number;
  status: number;
  paymentMethodType?: number;
  stripeSubscriptionId?: string;
  quoteGuid: string;
  programId: number;
  program?: any;
  insuranceCompanyId?: number;
  insuranceCompany?: any;
  createdByProducerId: number;
  updatedByProducerId?: number;
  policies: any[];
  totalFinancingAmount: number;
}

export interface QuoteListRequest {
  offset: number;
  pageSize: number;
  status?: number;
  producerId?: number;
  searchText?: string;
  /**
   * KAN-29 · Quotes vs Accounts segmented control.
   *  - 'quotes'   → open quotes (Status == Quoted)
   *  - 'accounts' → accepted accounts (Status != Quoted)
   *  - 'all'      → both (default)
   * Passed straight through as the `view` query param.
   */
  view?: QuoteListView;
}

export type QuoteListView = 'quotes' | 'accounts' | 'all';

export interface QuoteListResponse {
  quotes: Quote[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface SendCustomerLinkRequest {
  customerEmail: string;
  customerName: string;
}

export interface SendCustomerLinkResponse {
  quoteId: number;
  customerLink: string;
  emailSent: boolean;
  message: string;
}

export interface GetCustomerLinkResponse {
  quoteId: number;
  customerLink: string;
}

// ---------------------------------------------------------------------------
//  KAN-29 · Account detail aggregate — GET /quotes/{id}/account
// ---------------------------------------------------------------------------

export interface ScheduleInstallment {
  installment: number;
  dueDate: string;
  amount: number;
  monthlyFinancingPayment?: number;
  adminFeeInstallment?: number;
}

export interface QuoteSchedule {
  quoteId: number;
  downPayment: number;
  amountFinanced: number;
  monthlyPayment: number;
  term: number;
  rate: number;
  firstDueDate: string;
  installments: ScheduleInstallment[];
  monthlyFinancingPayment?: number;
  adminFeeAmount?: number;
  adminFeeInstallments?: number[];
}

export interface AccountFinancing {
  downPayment: number;
  amountFinanced: number;
  monthlyPayment: number;
  rate: number;
  term: number;
  totalOfPayments: number;
  monthlyFinancingPayment?: number;
  adminFeeAmount?: number;
}

export interface AccountPayment {
  id?: number;
  paymentDate: string;
  stripePaymentId?: string;
  stripeInvoiceUrl?: string;
  status: string;
  amount: number;
  netAmount?: number;
  paymentType?: number;
  createdAt?: string;
}

// IFL-34 · locked policy line-item embedded in the account aggregate.
export interface AccountPolicy {
  policyNumber: string;
  coverage: number;
  effectiveDate: string;
  expirationDate: string;
  premiumAmount: number;
  taxesAmount: number;
  feesAmount: number;
  totalPremium: number;
  downAmount: number;
}

export interface AccountDetail {
  quoteId: number;
  insuredName: string;
  agentName: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  customerNumber?: string;
  loanType: number;
  status: number;
  programName?: string;
  financing: AccountFinancing;
  schedule: QuoteSchedule;
  payments: AccountPayment[];
  amountPaid: number;
  balanceRemaining: number;
  nextDueDate?: string | null;
  nextAmount?: number | null;
  graceStartedAt?: string | null;
  delinquentStartedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  createdByProducerName?: string | null;
  outstandingFees?: number;
  policies?: AccountPolicy[];   // IFL-34 · embedded so the screen needs no separate quote fetch
  agreementSignedAt?: string | null;      // KAN-5 · when the customer signed
  agreementCompletedAt?: string | null;   // KAN-5 · when fully executed (all signers)
  docuSignStatusRaw?: string | null;      // KAN-5 · last-seen DocuSign envelope status
}

@Injectable({
  providedIn: 'root'
})
export class QuoteService {
  private readonly quoteEndpoint = '/api/v1/quotes';

  constructor(
    private apiService: ApiService,
    private authService: AuthService
  ) {}

  /**
   * Get all quotes with pagination
   */
  getQuotes(request: QuoteListRequest): Observable<QuoteListResponse> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }

    const params = new URLSearchParams();
    params.set('offset', request.offset.toString());
    params.set('pageSize', request.pageSize.toString());
    if (request.view) {
      params.set('view', request.view);
    }
    if (request.status !== undefined) {
      params.set('status', request.status.toString());
    }
    if (request.producerId !== undefined && request.producerId !== null) {
      params.set('producerId', request.producerId.toString());
    }
    if (request.searchText !== undefined && request.searchText !== null && request.searchText.trim().length > 0) {
      params.set('searchText', request.searchText.trim());
    }

    return this.apiService.getWithAuth<QuoteListResponse>(
      `${this.quoteEndpoint}?${params.toString()}`,
      token
    );
  }

  /**
   * Download Premium Finance Agreement PDF (authenticated producer).
   */
  downloadQuotePdf(id: number): Observable<Blob> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }

    return this.apiService.getBlobWithAuth(`${this.quoteEndpoint}/${id}/pdf`, token);
  }

  /**
   * Get quote by ID
   */
  getQuote(id: number): Observable<Quote> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }

    return this.apiService.getWithAuth<Quote>(`${this.quoteEndpoint}/${id}`, token);
  }

  /**
   * KAN-29 · Get the account aggregate for an accepted quote.
   * Returns status, financing, schedule, payments[], amountPaid,
   * balanceRemaining and next-payment / grace / delinquent markers.
   */
  getAccount(id: number): Observable<AccountDetail> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }

    return this.apiService.getWithAuth<AccountDetail>(`${this.quoteEndpoint}/${id}/account`, token);
  }

  /**
   * KAN-5 phase 2 · get an embedded signing URL for the logged-in producer to sign as agent.
   * The backend stamps the producer onto recipient 2 before returning the URL.
   */
  createAgentSigningUrl(quoteId: number): Observable<{ url: string }> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }
    return this.apiService.postWithAuth<{ url: string }>('/api/v1/docusign/agent-signing-url', { quoteId }, token);
  }

  /** KAN-5 phase 2 · whether the agreement is fully executed (agent has signed). */
  getAgentSigningStatus(quoteId: number): Observable<{ completed: boolean }> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }
    return this.apiService.getWithAuth<{ completed: boolean }>(`/api/v1/docusign/agent-signing-status?quoteId=${quoteId}`, token);
  }

  /**
   * KAN-29 · Get the amortization schedule for a quote.
   */
  getSchedule(id: number): Observable<QuoteSchedule> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }

    return this.apiService.getWithAuth<QuoteSchedule>(`${this.quoteEndpoint}/${id}/schedule`, token);
  }

  /**
   * Create new quote
   */
  createQuote(quoteData: CreateQuoteRequest): Observable<Quote> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }

    return this.apiService.postWithAuth<Quote>(this.quoteEndpoint, quoteData, token);
  }

  /**
   * Update quote
   */
  updateQuote(id: number, quoteData: Partial<CreateQuoteRequest>): Observable<Quote> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }

    return this.apiService.putWithAuth<Quote>(`${this.quoteEndpoint}/${id}`, quoteData, token);
  }

  /**
   * Delete quote
   */
  deleteQuote(id: number): Observable<void> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }

    return this.apiService.deleteWithAuth<void>(`${this.quoteEndpoint}/${id}`, token);
  }

  /**
   * KAN-43 · Re-issue an expired offer: mint a fresh customer link + restart the 30-day clock.
   */
  reissueQuote(id: number): Observable<{ id: number; quoteGuid: string; expiresAt: string }> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }

    return this.apiService.postWithAuth<{ id: number; quoteGuid: string; expiresAt: string }>(
      `${this.quoteEndpoint}/${id}/reissue`, {}, token);
  }

  /**
   * Get customer quote link without sending email
   */
  getCustomerLink(quoteId: number): Observable<GetCustomerLinkResponse> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }

    return this.apiService.getWithAuth<GetCustomerLinkResponse>(
      `${this.quoteEndpoint}/${quoteId}/customer-link`,
      token
    );
  }

  /**
   * Send customer quote link via email
   */
  sendCustomerLink(quoteId: number, request: SendCustomerLinkRequest): Observable<SendCustomerLinkResponse> {
    const token = this.authService.getToken();
    if (!token) {
      return throwError(() => new Error('User not authenticated'));
    }

    return this.apiService.postWithAuth<SendCustomerLinkResponse>(
      `${this.quoteEndpoint}/${quoteId}/send-customer-link`,
      request,
      token
    );
  }
}
